import React, { useCallback } from 'react';
import { Wrench } from 'lucide-react';
import type { ExistingEquipmentSection } from '@officehub/shared';
import { INPUT_CLASS, LABEL_CLASS, TOGGLE_ACTIVE, TOGGLE_INACTIVE } from './styles';
import type { RenderSectionPhotoCapture } from './photoCapture';

export interface ExistingEquipmentFormProps {
  data: ExistingEquipmentSection;
  onUpdate: (d: Partial<ExistingEquipmentSection>) => void;
  /**
   * Optional host-supplied photo capture panel. When omitted the Photos block
   * is not rendered (structured-data-only capture).
   */
  renderPhotoCapture?: RenderSectionPhotoCapture;
}

const TOGGLE_BASE = 'flex-1 py-2.5 text-xs rounded-xl transition-all duration-150 min-h-[48px]';

export function ExistingEquipmentForm({
  data,
  onUpdate,
  renderPhotoCapture,
}: ExistingEquipmentFormProps): React.ReactElement {
  const handleChange = useCallback(
    (field: keyof ExistingEquipmentSection, value: unknown) => {
      onUpdate({ [field]: value });
    },
    [onUpdate],
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Section header */}
      <div className="flex items-center gap-2 px-1">
        <Wrench className="w-3.5 h-3.5 text-slate-400" />
        <h2 className="text-[11px] font-black text-slate-500 uppercase tracking-[0.1em]">
          Existing Equipment
        </h2>
        <div className="flex-1 h-px bg-gradient-to-r from-slate-200 to-transparent" />
      </div>

      {/* Has Existing Generator toggle */}
      <div>
        <label className={LABEL_CLASS}>Has Existing Generator</label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => handleChange('hasExistingGenerator', true)}
            className={`${TOGGLE_BASE} ${data.hasExistingGenerator ? TOGGLE_ACTIVE : TOGGLE_INACTIVE}`}
          >
            Yes
          </button>
          <button
            type="button"
            onClick={() => handleChange('hasExistingGenerator', false)}
            className={`${TOGGLE_BASE} ${!data.hasExistingGenerator ? TOGGLE_ACTIVE : TOGGLE_INACTIVE}`}
          >
            No
          </button>
        </div>
      </div>

      {/* Conditional fields — only when hasExistingGenerator is true */}
      {data.hasExistingGenerator && (
        <>
          {/* Existing Generator Info */}
          <div>
            <label className={LABEL_CLASS}>Generator Info</label>
            <input
              type="text"
              className={INPUT_CLASS}
              value={data.existingGeneratorInfo ?? ''}
              onChange={(e) => handleChange('existingGeneratorInfo', e.target.value)}
              placeholder="Make, model, kW, age"
            />
          </div>

          {/* Disposal Needed toggle */}
          <div>
            <label className={LABEL_CLASS}>Disposal Needed</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleChange('disposalNeeded', true)}
                className={`${TOGGLE_BASE} ${data.disposalNeeded ? TOGGLE_ACTIVE : TOGGLE_INACTIVE}`}
              >
                Yes
              </button>
              <button
                type="button"
                onClick={() => handleChange('disposalNeeded', false)}
                className={`${TOGGLE_BASE} ${!data.disposalNeeded ? TOGGLE_ACTIVE : TOGGLE_INACTIVE}`}
              >
                No
              </button>
            </div>
          </div>
        </>
      )}

      {/* Photos — only when the host supplies a capture panel */}
      {renderPhotoCapture && (
        <div>
          <div className="flex items-center gap-2 mb-3 px-1">
            <h3 className="text-[11px] font-black text-slate-500 uppercase tracking-[0.1em]">Photos</h3>
            <div className="flex-1 h-px bg-gradient-to-r from-slate-200 to-transparent" />
          </div>
          {renderPhotoCapture({
            section: 'existingEquipment',
            photos: data.photos,
            availableTags: [{ value: 'existing_equipment', label: 'Existing Equipment' }],
          })}
        </div>
      )}
    </div>
  );
}
